import { cva, type VariantProps } from "class-variance-authority";
import { Image as ImageIcon } from "lucide-react";
import NextImage from "next/image";
import { cn } from "../../lib/utils";

const titleContainerVariants = cva(
  "w-full min-w-0 max-w-full overflow-hidden",
  {
    variants: {
      variant: {
        simple: "flex flex-row items-center gap-4",
        image: "flex flex-col gap-4 md:flex-row md:items-center md:gap-6",
        cover:
          "relative flex min-h-[240px] flex-col justify-end rounded-xl bg-muted",
        profile: "flex flex-col items-center gap-4 text-center",
      },
      size: {
        default: "",
        compact: "",
      },
    },
    compoundVariants: [
      {
        variant: "simple",
        size: "default",
        className: "py-4",
      },
      {
        variant: "simple",
        size: "compact",
        className: "py-2",
      },
      {
        variant: "cover",
        size: "compact",
        className: "min-h-[200px]",
      },
      {
        variant: "profile",
        size: "default",
        className: "py-6",
      },
      {
        variant: "profile",
        size: "compact",
        className: "py-4",
      },
    ],
    defaultVariants: {
      variant: "simple",
      size: "default",
    },
  }
);

const titleImageVariants = cva("max-w-full flex-shrink-0", {
  variants: {
    variant: {
      simple: "h-[120px] w-[120px] rounded-xl",
      image: "w-full rounded-2xl md:h-[280px] md:w-[40%] md:max-w-[392px]",
      cover: "absolute inset-0 h-full w-full rounded-xl object-cover",
      profile: "mx-auto rounded-full",
    },
    size: {
      default: "",
      compact: "",
    },
    imageFill: {
      fill: "bg-muted object-cover",
      fit: "bg-muted object-contain p-4",
    },
  },
  compoundVariants: [
    {
      variant: "simple",
      size: "compact",
      className: "h-[70px] w-[70px]",
    },
    {
      variant: "image",
      size: "compact",
      className: "md:h-[200px] md:w-[40%] md:max-w-[280px]",
    },
    {
      variant: "profile",
      size: "default",
      className: "h-24 w-24",
    },
    {
      variant: "profile",
      size: "compact",
      className: "h-16 w-16",
    },
  ],
  defaultVariants: {
    variant: "simple",
    size: "default",
    imageFill: "fill",
  },
});

const titleContentVariants = cva("flex min-w-0 flex-col", {
  variants: {
    variant: {
      simple: "gap-2",
      image: "w-0 flex-1 justify-center gap-2",
      cover:
        "relative z-10 w-full flex-row items-end gap-4 rounded-b-xl bg-card px-6 pt-16 pb-6",
      profile: "gap-2",
    },
    size: {
      default: "",
      compact: "",
    },
  },
  compoundVariants: [
    {
      variant: "cover",
      size: "compact",
      className: "gap-3 px-4 pt-12 pb-4",
    },
  ],
  defaultVariants: {
    variant: "simple",
    size: "default",
  },
});

const emphasisTextVariants = cva(
  "mb-1 font-bold text-muted-foreground text-xs uppercase tracking-widest",
  {
    variants: {
      variant: {
        simple: "",
        image: "",
        cover: "",
        profile: "",
      },
      size: {
        default: "",
        compact: "",
      },
    },
    defaultVariants: {
      variant: "simple",
      size: "default",
    },
  }
);

const titleTextVariants = cva(
  "font-bold text-foreground leading-tight tracking-tight",
  {
    variants: {
      variant: {
        simple: "text-2xl",
        image: "text-3xl md:text-4xl",
        cover: "text-2xl",
        profile: "text-3xl md:text-4xl",
      },
      size: {
        default: "",
        compact: "",
      },
    },
    compoundVariants: [
      {
        variant: "simple",
        size: "compact",
        className: "text-lg",
      },
      {
        variant: "image",
        size: "compact",
        className: "text-2xl md:text-3xl",
      },
      {
        variant: "cover",
        size: "compact",
        className: "text-xl",
      },
      {
        variant: "profile",
        size: "compact",
        className: "text-2xl md:text-3xl",
      },
    ],
    defaultVariants: {
      variant: "simple",
      size: "default",
    },
  }
);

const subtitleTextVariants = cva("text-muted-foreground leading-relaxed", {
  variants: {
    variant: {
      simple: "text-base",
      image: "text-lg",
      cover: "text-base",
      profile: "text-lg",
    },
    size: {
      default: "",
      compact: "",
    },
  },
  compoundVariants: [
    {
      variant: "simple",
      size: "compact",
      className: "text-sm",
    },
    {
      variant: "image",
      size: "compact",
      className: "text-base",
    },
    {
      variant: "cover",
      size: "compact",
      className: "text-sm",
    },
    {
      variant: "profile",
      size: "compact",
      className: "text-base",
    },
  ],
  defaultVariants: {
    variant: "simple",
    size: "default",
  },
});

const imagePlaceholderVariants = cva(
  "flex items-center justify-center bg-muted text-muted-foreground",
  {
    variants: {
      variant: {
        simple: "h-[120px] w-[120px] rounded-xl",
        image: "w-full rounded-2xl md:h-[280px] md:w-[40%] md:max-w-[392px]",
        cover: "absolute inset-0 h-full w-full rounded-xl",
        profile: "mx-auto rounded-full",
      },
      size: {
        default: "",
        compact: "",
      },
    },
    compoundVariants: [
      {
        variant: "simple",
        size: "compact",
        className: "h-[70px] w-[70px]",
      },
      {
        variant: "image",
        size: "compact",
        className: "md:h-[200px] md:w-[40%] md:max-w-[280px]",
      },
      {
        variant: "profile",
        size: "default",
        className: "h-24 w-24",
      },
      {
        variant: "profile",
        size: "compact",
        className: "h-16 w-16",
      },
    ],
    defaultVariants: {
      variant: "simple",
      size: "default",
    },
  }
);

export interface TitleProps
  extends VariantProps<typeof titleContainerVariants> {
  className?: string;
  coverImageAlt?: string;
  coverImageUrl?: string;
  emphasis?: string;
  imageAlt?: string;
  imageFill?: "fill" | "fit";
  imageUrl?: string;
  subtitle?: string;
  title: string;
  truncate?: boolean;
}

function getImageDimensions(
  variant: TitleProps["variant"],
  size: TitleProps["size"]
) {
  if (variant === "simple") {
    return size === "compact"
      ? { width: 70, height: 70 }
      : { width: 120, height: 120 };
  }
  if (variant === "image") {
    return size === "compact"
      ? { width: 280, height: 200 }
      : { width: 392, height: 280 };
  }
  if (variant === "profile") {
    return size === "compact"
      ? { width: 64, height: 64 }
      : { width: 96, height: 96 };
  }
  return { width: 800, height: 400 };
}

function TitleTextContent({
  title,
  subtitle,
  emphasis,
  variant,
  size,
  truncate,
}: {
  title: string;
  subtitle?: string;
  emphasis?: string;
  variant: TitleProps["variant"];
  size: TitleProps["size"];
  truncate?: boolean;
}) {
  return (
    <>
      {emphasis && (
        <p className={cn(emphasisTextVariants({ variant, size }))}>
          {emphasis}
        </p>
      )}
      {title && (
        <h1
          className={cn(
            titleTextVariants({ variant, size }),
            truncate && "truncate"
          )}
        >
          {title}
        </h1>
      )}
      {subtitle && (
        <p
          className={cn(
            subtitleTextVariants({ variant, size }),
            truncate && "truncate"
          )}
        >
          {subtitle}
        </p>
      )}
    </>
  );
}

function CoverBanner({
  coverImageUrl,
  coverImageAlt,
  isCompact,
}: {
  coverImageUrl?: string;
  coverImageAlt?: string;
  isCompact: boolean;
}) {
  return (
    <div className={cn("relative bg-muted", isCompact ? "h-24" : "h-32")}>
      {coverImageUrl ? (
        <NextImage
          alt={coverImageAlt || "Cover image"}
          className="h-full w-full object-cover"
          fill
          src={coverImageUrl}
        />
      ) : (
        <div
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center"
        >
          <ImageIcon className="h-10 w-10 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

function CoverAvatar({
  imageUrl,
  imageAlt,
  title,
  isCompact,
}: {
  imageUrl?: string;
  imageAlt?: string;
  title: string;
  isCompact: boolean;
}) {
  const sizeClass = isCompact ? "-mt-8 h-16 w-16" : "-mt-12 h-24 w-24";
  const baseClass =
    "flex-shrink-0 rounded-full border-4 border-card bg-muted shadow-sm";

  if (imageUrl) {
    return (
      <NextImage
        alt={imageAlt || title}
        className={cn(baseClass, "object-cover", sizeClass)}
        height={isCompact ? 64 : 96}
        src={imageUrl}
        width={isCompact ? 64 : 96}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      className={cn(
        baseClass,
        "flex items-center justify-center text-muted-foreground",
        sizeClass
      )}
    >
      <ImageIcon
        className={cn(
          "text-muted-foreground",
          isCompact ? "h-6 w-6" : "h-8 w-8"
        )}
      />
    </div>
  );
}

function CoverVariant({
  title,
  subtitle,
  emphasis,
  imageUrl,
  coverImageUrl,
  imageAlt,
  coverImageAlt,
  size,
  className,
  truncate,
}: TitleProps & { variant: "cover" }) {
  const isCompact = size === "compact";

  return (
    <div
      className={cn(
        "w-full min-w-0 max-w-full overflow-hidden rounded-2xl",
        className
      )}
    >
      <CoverBanner
        coverImageAlt={coverImageAlt}
        coverImageUrl={coverImageUrl}
        isCompact={isCompact}
      />

      <div
        className={cn(
          "relative flex flex-col items-center bg-card px-6 text-center",
          isCompact ? "gap-1 pb-4" : "gap-2 pb-6"
        )}
      >
        <CoverAvatar
          imageAlt={imageAlt}
          imageUrl={imageUrl}
          isCompact={isCompact}
          title={title}
        />

        <TitleTextContent
          emphasis={emphasis}
          size={size}
          subtitle={subtitle}
          title={title}
          truncate={truncate}
          variant="cover"
        />
      </div>
    </div>
  );
}

/**
 * @deprecated Use SectionHeader instead for page/section headers.
 *
 * Migration guide:
 * - For section headers: Title → SectionHeader
 * - SectionHeader is a simpler, focused component (~100 lines vs 565 lines)
 *
 * This component will be removed in v2.0.0
 */
export function Title({
  title,
  emphasis,
  subtitle,
  imageUrl,
  coverImageUrl,
  imageFill = "fill",
  imageAlt = "",
  coverImageAlt = "",
  variant = "simple",
  size = "default",
  className,
  truncate = false,
}: TitleProps) {
  // Add console warning in development
  if (process.env.NODE_ENV === "development") {
    console.warn(
      "Title component is deprecated. Use SectionHeader instead. See migration guide in component docs."
    );
  }

  if (variant === "cover") {
    return (
      <CoverVariant
        className={className}
        coverImageAlt={coverImageAlt}
        coverImageUrl={coverImageUrl}
        emphasis={emphasis}
        imageAlt={imageAlt}
        imageFill={imageFill}
        imageUrl={imageUrl}
        size={size}
        subtitle={subtitle}
        title={title}
        truncate={truncate}
        variant="cover"
      />
    );
  }

  const hasImage = Boolean(imageUrl);
  const showImage =
    (variant === "simple" && hasImage) ||
    (variant !== "simple" &&
      (hasImage || variant === "image" || variant === "profile"));

  const { width, height } = getImageDimensions(variant, size);

  return (
    <div className={cn(titleContainerVariants({ variant, size }), className)}>
      {showImage &&
        (hasImage && imageUrl ? (
          <NextImage
            alt={imageAlt || title}
            className={cn(titleImageVariants({ variant, size, imageFill }))}
            height={height}
            src={imageUrl}
            width={width}
          />
        ) : (
          <div
            aria-hidden="true"
            className={cn(imagePlaceholderVariants({ variant, size }))}
          >
            <ImageIcon
              className={cn(
                "text-muted-foreground",
                size === "compact" ? "h-8 w-8" : "h-12 w-12"
              )}
            />
          </div>
        ))}

      <div className={cn(titleContentVariants({ variant, size }))}>
        <TitleTextContent
          emphasis={emphasis}
          size={size}
          subtitle={subtitle}
          title={title}
          truncate={truncate}
          variant={variant}
        />
      </div>
    </div>
  );
}
